import { z } from "zod";

// ---- Twitch on the wire (docs/live-breaks.md, "Twitch") ----------------------------------
// Each environment's own Twitch app links its shoppers, shows channel status and decides
// priority; without one, every Twitch route answers that it is off. Tokens never reach the wire:
// a shopper's link is their login, their display name and whether it still works. This file reads
// nothing from `index.ts`, which re-exports it.

/** Twitch's login grammar, after `canonicalTwitchLogin` (legacy logins are under four characters). */
export const TWITCH_LOGIN_PATTERN = /^[a-z0-9_]{1,25}$/;
/** Most logins one channel lookup takes (Helix's cap per `/users` and `/streams` call). */
export const TWITCH_CHANNEL_LOOKUP_MAX = 100;
/** A sponsored stream's staff name, at most this long (boxed's `DISPLAY_NAME_MAX_LENGTH`). */
export const SPONSORED_STREAM_DISPLAY_NAME_MAX = 80;
/** A show lasts 1 to this many whole hours (boxed's `MAX_SHOW_HOURS`). */
export const SPONSORED_STREAM_MAX_SHOW_HOURS = 72;

/** A login as stored and compared: trimmed and lowercase. */
export function canonicalTwitchLogin(input: string): string {
  return input.trim().toLowerCase();
}

/** The canonical login, or null when it is not one Twitch could have (boxed's `parseTwitchLogin`). */
export function parseTwitchLogin(input: string): string | null {
  const login = canonicalTwitchLogin(input);
  return TWITCH_LOGIN_PATTERN.test(login) ? login : null;
}

/** A channel's page on Twitch. */
export function twitchChannelUrl(login: string): string {
  return `https://twitch.tv/${login}`;
}

/** Whether a shopper's link still works: `reconnect` = it was revoked or lacks its scope; link again. */
export const TWITCH_IDENTITY_STATUSES = ["linked", "reconnect"] as const;
export type TwitchIdentityStatus = (typeof TWITCH_IDENTITY_STATUSES)[number];

/**
 * A shopper's own linked Twitch account (boxed's `TwitchIdentity`). `status` is the server's word
 * on its token alone: it says nothing about the browser's Twitch session or the embedded player.
 */
export const twitchIdentitySchema = z.object({
  /** Lowercase, as it was when linked. */
  login: z.string(),
  displayName: z.string().nullable(),
  status: z.enum(TWITCH_IDENTITY_STATUSES),
});
export type TwitchIdentity = z.infer<typeof twitchIdentitySchema>;

/** A Twitch channel as Twitch last reported it, at most a minute old (boxed's `TwitchChannel`). */
export const twitchChannelSchema = z.object({
  /** Canonical (lowercase). */
  login: z.string(),
  /** False when Twitch has no user by this login; the other fields are then null or false. */
  isFound: z.boolean(),
  displayName: z.string().nullable(),
  avatarUrl: z.string().nullable(),
  isLive: z.boolean(),
  /** Null when not live. */
  viewerCount: z.int().nullable(),
});
export type TwitchChannel = z.infer<typeof twitchChannelSchema>;

/**
 * A live break's channel as the cache last saw it, display only (never a transition); null on the
 * live break when it shows no channel, the store has no Twitch app, or nothing has read the
 * channel from Twitch yet (`GET /v1/twitch/channels` does).
 */
export const liveBreakTwitchStatusSchema = twitchChannelSchema.pick({
  login: true,
  displayName: true,
  avatarUrl: true,
  isLive: true,
  viewerCount: true,
});
export type LiveBreakTwitchStatus = z.infer<typeof liveBreakTwitchStatusSchema>;

/**
 * A staff-listed streamer shown now (boxed's public `SponsoredStream`): the widget's sponsored row
 * while they host a live break, its promoted stream while they are live on Twitch without one, and
 * the priority note's name for their channel. When it stops showing is staff-only.
 */
export const liveBreakSponsoredStreamSchema = z.object({
  id: z.string(),
  /** Lowercase Twitch login. */
  twitchLogin: z.string(),
  /** Lower shows first; not unique. */
  priority: z.int(),
  /** Staff's name for the channel; null = show Twitch's display name, else the login. */
  displayName: z.string().nullable(),
  /** `https://twitch.tv/{twitchLogin}`. */
  twitchUrl: z.string(),
});
export type LiveBreakSponsoredStream = z.infer<typeof liveBreakSponsoredStreamSchema>;

// ---- requests ------------------------------------------------------------------------------------

/** GET /v1/twitch/channels?logins=a,b: 1–100 logins, canonicalized and de-duplicated in order. */
export const twitchChannelQuerySchema = z.object({
  logins: z
    .string()
    .transform((raw) =>
      raw
        .split(",")
        .map((part) => part.trim())
        .filter((part) => part.length > 0),
    )
    .refine(
      (parts) => parts.length >= 1 && parts.length <= TWITCH_CHANNEL_LOOKUP_MAX,
      `1 to ${TWITCH_CHANNEL_LOOKUP_MAX} logins`,
    )
    .refine(
      (parts) => parts.every((part) => parseTwitchLogin(part) !== null),
      "each login is 1-25 letters, digits or _",
    )
    .transform((parts) => [...new Set(parts.map(canonicalTwitchLogin))]),
});

/** POST /v1/me/twitch/link: what the storefront's callback got back from Twitch. */
export const linkTwitchAccountRequestSchema = z.object({
  /** The authorization code from Twitch's redirect. */
  code: z.string().min(1).max(512),
  /** The exact redirect URI the code was issued for, registered on the store's Twitch app. */
  redirectUri: z.string().min(1).max(2048),
});
export type LinkTwitchAccountRequest = z.infer<typeof linkTwitchAccountRequestSchema>;

// ---- responses -------------------------------------------------------------------------------------

/** GET /v1/twitch/channels: one per distinct login, in the order asked. */
export const twitchChannelListResponseSchema = z.object({ data: z.array(twitchChannelSchema) });
export type TwitchChannelListResponse = z.infer<typeof twitchChannelListResponseSchema>;

/**
 * GET /v1/me/twitch: whether the store has Twitch at all, and the caller's link (validated when
 * due, so the read may write). `enabled: false` always comes with `identity: null`.
 */
export const twitchMeResponseSchema = z.object({
  enabled: z.boolean(),
  identity: twitchIdentitySchema.nullable(),
});
export type TwitchMeResponse = z.infer<typeof twitchMeResponseSchema>;

/** POST /v1/me/twitch/link */
export const twitchLinkResponseSchema = z.object({ identity: twitchIdentitySchema });
export type TwitchLinkResponse = z.infer<typeof twitchLinkResponseSchema>;

/** DELETE /v1/me/twitch */
export const twitchUnlinkResponseSchema = z.object({ unlinked: z.literal(true) });
export type TwitchUnlinkResponse = z.infer<typeof twitchUnlinkResponseSchema>;

/** GET /v1/live-breaks/sponsored-streams: shown now, priority first, then login. */
export const liveBreakSponsoredStreamListResponseSchema = z.object({
  data: z.array(liveBreakSponsoredStreamSchema),
});
export type LiveBreakSponsoredStreamListResponse = z.infer<
  typeof liveBreakSponsoredStreamListResponseSchema
>;
